import { memo } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';

import { ContentModalContentType, ContentTypeForComparison, ContentTypeEnumForBackend } from 'domains/content/types';
import { FolderModalTypes } from 'domains/core/components/folders/types';
import MoreOptionsDropdown from 'domains/core/components/MoreOptionsDropdown';
import useUpdateContentStatus from 'hooks/mutations/useUpdateContentStatus';
import useContentQueryManager from 'hooks/useContentQueryParams';
import { removeTypeFromId } from 'utils/folders';

import { ContentStatus } from '../ContentStatusText';
import { getContentCreatePath, contentTypeToSnakeCase } from '../utils';

import type { MenuItemProps } from 'domains/core/components/MoreOptionsDropdown/MoreOptionsDropdown';

export type ContentTableOptionsCellProps = {
    handleModalOpen: (content: ContentModalContentType, modalType: FolderModalTypes) => void;
    val: any;
    /** When false, hides Edit / Copy / Move / Archive / Delete / Rename (content.manage). Default true. */
    canManageContent?: boolean;
};

/**
 * A memoized component that renders the options dropdown cell for each content/folder row.
 */
const ContentTableOptionsCell = ({ handleModalOpen, val, canManageContent = true }: ContentTableOptionsCellProps) => {
    const { contentDraft: hasContentDraft } = useFlags();
    const history = useHistory();
    const { changeViewParams, folderId: parentFolderId } = useContentQueryManager();

    const handleDeleteModalOpen = (contentData: ContentModalContentType) =>
        handleModalOpen(contentData, FolderModalTypes.DELETE);

    const handleArchiveModalOpen = (contentData: ContentModalContentType) => {
        handleModalOpen(contentData, FolderModalTypes.ARCHIVE);
    };

    const handleUnarchiveModalOpen = (contentData: ContentModalContentType) => {
        handleModalOpen(contentData, FolderModalTypes.UNARCHIVE);
    };

    const shouldShowDeleteOption = val.row.original.canDelete;

    const contentId = removeTypeFromId(val.row.original.id);
    const contentData = {
        ...val.row.original,
        id: contentId,
    };

    const status = val.row.original.status;
    const isPublished = status === ContentStatus.PUBLISHED;
    const contentType = val.row.original.type;

    const { mutate: updateContentStatus } = useUpdateContentStatus(contentId.toString(), {
        onSuccess: () => {
            if (val.row.original.type !== ContentTypeForComparison.FOLDER) {
                // Navigate to builder page - status will be fetched from the content API
                const path = getContentCreatePath({
                    contentType: val.row.original.type,
                    id: contentId,
                    parentFolderId,
                    status: ContentStatus.DRAFT,
                });
                history.push(path);
            }
        },
    });

    const deleteOption = {
        name: 'Delete',
        onClick: () => handleDeleteModalOpen(contentData),
    };

    const archiveOption = {
        name: 'Archive',
        onClick: () => handleArchiveModalOpen(contentData),
    };

    const unarchiveOption = {
        name: 'Unarchive',
        onClick: () => handleUnarchiveModalOpen(contentData),
    };

    const menuItems: MenuItemProps[] = ((): MenuItemProps[] => {
        if (val.row.original.type === ContentTypeForComparison.FOLDER) {
            const openOption: MenuItemProps = {
                name: 'Open',
                onClick: () => changeViewParams({ newFolderId: contentId }),
            };
            const renameOption: MenuItemProps = {
                name: 'Rename',
                onClick: () => handleModalOpen(contentData, FolderModalTypes.RENAME),
            };
            if (!canManageContent) {
                return [openOption];
            }
            return val.row.original.canDelete ? [openOption, renameOption, deleteOption] : [openOption, renameOption];
        }

        const editOption: MenuItemProps = {
            name: 'Edit',
            onClick: () => {
                if (isPublished && hasContentDraft) {
                    const channelForBackend = contentTypeToSnakeCase(
                        contentType as keyof typeof ContentTypeEnumForBackend
                    );

                    updateContentStatus({
                        status: ContentStatus.DRAFT,
                        channel: channelForBackend,
                    });
                } else {
                    const path = getContentCreatePath({
                        contentType,
                        id: contentId,
                        parentFolderId,
                        status,
                    });
                    history.push(path);
                }
            },
        };
        const copyOption: MenuItemProps = {
            name: 'Copy',
            onClick: () => {
                const path = getContentCreatePath({
                    contentType: val.row.original.type,
                    id: contentId,
                    isCopy: true,
                    parentFolderId,
                });
                history.push(path);
            },
        };
        const moveOption: MenuItemProps = {
            name: 'Move',
            onClick: () => {
                handleModalOpen(val.row.original, FolderModalTypes.MOVE);
            },
        };

        const shouldShowArchiveOption = val.row.original.type !== 'Folder';
        const isArchived = val.row.original.isArchived;
        const isLive = status === ContentStatus.LIVE;

        let archiveMenuItems: MenuItemProps[] = [];
        if (shouldShowArchiveOption) {
            archiveMenuItems = isArchived ? [unarchiveOption] : [archiveOption];
        }

        const shouldShowMoveOption = !isArchived;
        const moveMenuItems: MenuItemProps[] = shouldShowMoveOption ? [moveOption] : [];

        if (!canManageContent) {
            return [];
        }
        if (isLive) {
            return shouldShowMoveOption ? [copyOption, moveOption] : [copyOption];
        }
        return shouldShowDeleteOption
            ? [editOption, copyOption, ...moveMenuItems, ...archiveMenuItems, deleteOption]
            : [editOption, ...moveMenuItems, copyOption, ...archiveMenuItems];
    })();

    if (!menuItems.length) {
        return null;
    }

    return <MoreOptionsDropdown buttonTestId="content-more-options" menuItems={menuItems} />;
};

export default memo(ContentTableOptionsCell);
